import type { Mission, Agv, WarehouseLayout, AgvLog } from "./types";

export const initialMissions: Mission[] = [
    {
        id: "m1",
        name: "Transport Pallet #1042",
        assignedAgvId: "agv-1",
        status: "in_progress",
        from: "Pickup A",
        to: "Dropoff B",
        createdAt: "2026-02-15T08:30:00Z",
    },
    {
        id: "m2",
        name: "Return Empty Pallet #987",
        assignedAgvId: "agv-2",
        status: "in_progress",
        from: "Dropoff B",
        to: "Charging Bay",
        createdAt: "2026-02-15T08:45:00Z",
    },
    {
        id: "m3",
        name: "Transport Pallet #1043",
        assignedAgvId: null,
        status: "pending",
        from: "Pickup A",
        to: "Storage Rack 1",
        createdAt: "2026-02-15T09:00:00Z",
    },
    {
        id: "m4",
        name: "Emergency Retrieval #501",
        assignedAgvId: "agv-4",
        status: "failed",
        from: "Storage Rack 2",
        to: "Dropoff B",
        createdAt: "2026-02-15T07:15:00Z",
    },
    {
        id: "m5",
        name: "Inventory Move #220",
        assignedAgvId: "agv-5",
        status: "in_progress",
        from: "Storage Rack 1",
        to: "Storage Rack 2",
        createdAt: "2026-02-15T09:10:00Z",
    },
    {
        id: "m6",
        name: "Transport Pallet #1044",
        assignedAgvId: null,
        status: "completed",
        from: "Pickup A",
        to: "Dropoff B",
        createdAt: "2026-02-15T06:00:00Z",
    },
];

export const initialAgvs: Agv[] = [
    {
        id: "agv-1",
        name: "AGV-01",
        manufacturer: "Flynt",
        model: "NaviBot 200",
        battery: 87,
        status: "moving",
        currentMissionId: "m1",
        position: { x: -17, z: 10 },
        rotation: 0,
        pathId: "p1",
        pathProgress: 0,
    },
    {
        id: "agv-2",
        name: "AGV-02",
        manufacturer: "Flynt",
        model: "NaviBot 300",
        battery: 52,
        status: "moving",
        currentMissionId: "m2",
        position: { x: 15.5, z: -8.5 },
        rotation: Math.PI,
        pathId: "p2",
        pathProgress: 0.5,
    },
    {
        id: "agv-3",
        name: "AGV-03",
        manufacturer: "Linde",
        model: "C-MATIC HP",
        battery: 95,
        status: "charging",
        currentMissionId: null,
        position: { x: 9, z: 6 },
        rotation: Math.PI,
        pathId: null,
        pathProgress: 0,
    },
    {
        id: "agv-4",
        name: "AGV-04",
        manufacturer: "KUKA",
        model: "KMP 1500",
        battery: 15,
        status: "errored",
        currentMissionId: "m4",
        position: { x: 1, z: -6 },
        rotation: Math.PI,
        pathId: null,
        pathProgress: 0,
    },
    {
        id: "agv-5",
        name: "AGV-05",
        manufacturer: "Flynt",
        model: "NaviBot 200",
        battery: 68,
        status: "moving",
        currentMissionId: "m5",
        position: { x: -17, z: 1.5 },
        rotation: 0,
        pathId: "p3",
        pathProgress: 0,
    },
    {
        id: "agv-6",
        name: "AGV-06",
        manufacturer: "Flynt",
        model: "NaviBot 200",
        battery: 99,
        status: "idle",
        currentMissionId: null,
        position: { x: 17, z: 0 },
        rotation: Math.PI,
        pathId: "p4",
        pathProgress: 0,
    },
];

export const warehouseLayout: WarehouseLayout = {
    floorWidth: 40,
    floorDepth: 25,
    zones: [
        { id: "z1", type: "forbidden", label: "No Entry", x: -14, z: -7, width: 4, depth: 5 },
        { id: "z2", type: "charging", label: "Charging Bay", x: 8, z: 4, width: 5, depth: 3 },
        { id: "z3", type: "pickup", label: "Pickup A", x: -9, z: 4, width: 3, depth: 3 },
        { id: "z4", type: "dropoff", label: "Dropoff B", x: 12, z: -6, width: 3, depth: 3 },
        { id: "z5", type: "storage", label: "Storage Rack 1", x: -2, z: -8, width: 6, depth: 3 },
        { id: "z6", type: "storage", label: "Storage Rack 2", x: -9, z: -8, width: 6, depth: 3 },
    ],
    obstacles: [
        { id: "o1", x: -2, z: -4, width: 6, depth: 2, height: 3, label: "Rack A" },
        { id: "o2", x: -9, z: -4, width: 6, depth: 2, height: 3, label: "Rack B" },
        { id: "o3", x: 3, z: 4, width: 4, depth: 3, height: 3, label: "Rack C" },
        { id: "o4", x: -14, z: 3, width: 4, depth: 4, height: 2, label: "Rack D" },
        { id: "o5", x: 7, z: -5, width: 3, depth: 3, height: 2.5, label: "Rack E" },
    ],
    paths: [
        {
            id: "p1",
            // Outer perimeter Lane A — clockwise
            points: [
                { x: -17, z: 10 },
                { x: 17, z: 10 },
                { x: 17, z: -10 },
                { x: -17, z: -10 },
                { x: -17, z: 10 },
            ],
        },
        {
            id: "p2",
            // Outer perimeter Lane B — clockwise, 1.5 units inside Lane A
            points: [
                { x: -15.5, z: 8.5 },
                { x: 15.5, z: 8.5 },
                { x: 15.5, z: -8.5 },
                { x: -15.5, z: -8.5 },
                { x: -15.5, z: 8.5 },
            ],
        },
        {
            id: "p3",
            // Middle cross-aisle Lane A — eastbound, intersects outer loops on both sides
            points: [
                { x: -17, z: 1.5 },
                { x: -15.5, z: 1.5 },
                { x: 15.5, z: 1.5 },
                { x: 17, z: 1.5 },
            ],
        },
        {
            id: "p4",
            // Middle cross-aisle Lane B — westbound, 1.5 units south of Lane A
            points: [
                { x: 17, z: 0 },
                { x: 15.5, z: 0 },
                { x: -15.5, z: 0 },
                { x: -17, z: 0 },
            ],
        },
    ],
};

export const logs: AgvLog[] = [
    {
        id: "b292db07-13ec-4b2b-b8cc-336b1c83fd7c",
        createdAt: new Date(2026, 1, 23, 14, 24, 55),
        message: "Mission Transport Pallet #1042 started",
        severity: "info",
    },
    {
        id: "447c74db-5226-4385-b9f0-536f2f90fc89",
        createdAt: new Date(2026, 1, 23, 13, 55, 1),
        message: "AGV-04 stopped due to malfunction",
        severity: "error",
    },
    {
        id: "ec74bcc7-bdbd-4128-9bcf-b2843736b38d",
        createdAt: new Date(2026, 1, 23, 13, 2, 12),
        message: "Mission Transport Pallet #1044 completed",
        severity: "success",
    },
    {
        id: "16841750-5bfc-4a3c-b343-9dabaa5653e8",
        createdAt: new Date(2026, 1, 23, 11, 34, 50),
        message: "Mission Return Empty Pallet #987 started",
        severity: "info",
    },
];
